import { api, unwrap } from '../../shared/api/client';
import type { components, operations } from '../../shared/api/schema.gen';

export type TFlow = components['schemas']['FlowResDto'];
export type TFlowList = components['schemas']['FlowListResDto'];
export type TFlowDraft = components['schemas']['FlowDraftResDto'];
export type TSaveDraft = components['schemas']['SaveDraftReqDto'];
export type TNodeCatalog = components['schemas']['NodeCatalogResDto'];
export type TDraftSamples = components['schemas']['DraftSamplesResDto'];
export type TFlowQuery = operations['FlowsController_list']['parameters']['query'];
export type TFlowCreate = components['schemas']['CreateFlowReqDto'];

export const listFlows = (query: TFlowQuery = {}): Promise<TFlowList> =>
  unwrap(api.GET('/v1/flows', { params: { query } }));

export const createFlow = (body: TFlowCreate): Promise<TFlow> =>
  unwrap(api.POST('/v1/flows', { body }));

export const getFlow = (id: string): Promise<TFlow> =>
  unwrap(api.GET('/v1/flows/{id}', { params: { path: { id } } }));

export const getDraft = (id: string): Promise<TFlowDraft> =>
  unwrap(api.GET('/v1/flows/{id}/draft', { params: { path: { id } } }));

// Optimistic: `revision` is the one the draft was loaded at; a save in between answers 409
// `flows.draft_conflict` with `details.currentRevision` instead of overwriting it.
export const saveDraft = (id: string, body: TSaveDraft): Promise<TFlowDraft> =>
  unwrap(api.PUT('/v1/flows/{id}/draft', { params: { path: { id } }, body }));

export type TPublishDraft = components['schemas']['PublishDraftReqDto'];
export type TFlowVersion = components['schemas']['FlowVersionResDto'];

export const publishDraft = (id: string, body: TPublishDraft): Promise<TFlowVersion> =>
  unwrap(api.POST('/v1/flows/{id}/draft/publish', { params: { path: { id } }, body }));

export const getDraftSamples = (id: string): Promise<TDraftSamples> =>
  unwrap(api.GET('/v1/flows/{id}/draft/samples', { params: { path: { id } } }));

export type TKnownFields = components['schemas']['KnownFieldsResDto'];
export type TSampleTrigger = components['schemas']['SampleTriggerResDto'];
export type TSampleTriggerQuery =
  operations['FlowDraftController_sampleTrigger']['parameters']['query'];

export const getKnownFields = (id: string): Promise<TKnownFields> =>
  unwrap(api.GET('/v1/flows/{id}/draft/known-fields', { params: { path: { id } } }));

export const getSampleTrigger = (id: string, query: TSampleTriggerQuery): Promise<TSampleTrigger> =>
  unwrap(api.GET('/v1/flows/{id}/draft/sample-trigger', { params: { path: { id }, query } }));

export type TFlowResultFields = components['schemas']['FlowResultFieldsResDto'];

export const getFlowResultFields = (id: string): Promise<TFlowResultFields> =>
  unwrap(api.GET('/v1/flows/{id}/result-fields', { params: { path: { id } } }));

export const getNodeCatalog = (): Promise<TNodeCatalog> => unwrap(api.GET('/v1/flows/node-types'));

export type TCredentialList = components['schemas']['CredentialListResDto'];

export const listCredentials = (): Promise<TCredentialList> => unwrap(api.GET('/v1/credentials'));

export type TFlowUpdate = components['schemas']['UpdateFlowReqDto'];
export type TFlowStateChange = components['schemas']['FlowStateChangeResDto'];
export type TFlowVersionGraph = components['schemas']['FlowVersionGraphResDto'];
export type TFlowDiff = components['schemas']['FlowDiffResDto'];
export type TCarryOver = components['schemas']['CarryOverResDto'];
export type TDraftConflict = components['schemas']['DraftConflictDto'];

export const updateFlow = (id: string, body: TFlowUpdate): Promise<TFlow> =>
  unwrap(api.PATCH('/v1/flows/{id}', { params: { path: { id } }, body }));

export const pauseFlow = (id: string): Promise<TFlowStateChange> =>
  unwrap(api.POST('/v1/flows/{id}/pause', { params: { path: { id } } }));

export const resumeFlow = (id: string): Promise<TFlowStateChange> =>
  unwrap(api.POST('/v1/flows/{id}/resume', { params: { path: { id } } }));

export const enableFlow = (id: string): Promise<TFlowStateChange> =>
  unwrap(api.POST('/v1/flows/{id}/enable', { params: { path: { id } } }));

// Without `stopWaiting` a paused flow holding copies answers 409 `flows.flow_has_waiting_copies`.
export const disableFlow = (id: string, stopWaiting: boolean): Promise<TFlowStateChange> =>
  unwrap(api.POST('/v1/flows/{id}/disable', { params: { path: { id } }, body: { stopWaiting } }));

export const deleteFlow = async (id: string, force: boolean): Promise<void> => {
  await unwrap(
    api.DELETE('/v1/flows/{id}', {
      params: { path: { id }, query: force ? { force: true } : {} },
    }),
  );
};

export type TFlowOpenCopies = components['schemas']['FlowOpenCopiesResDto'];

/** What a delete would stop now; the delete dialog asks before it offers the button. */
export const getOpenCopies = (id: string): Promise<TFlowOpenCopies> =>
  unwrap(api.GET('/v1/flows/{id}/open-copies', { params: { path: { id } } }));

export const listVersions = (id: string): Promise<TFlowVersion[]> =>
  unwrap(api.GET('/v1/flows/{id}/versions', { params: { path: { id } } }));

export const getVersion = (id: string, number: number | 'live'): Promise<TFlowVersionGraph> =>
  unwrap(
    api.GET('/v1/flows/{id}/versions/{number}', {
      params: { path: { id, number: String(number) } },
    }),
  );

export const getDiff = (id: string, from: string, to: string): Promise<TFlowDiff> =>
  unwrap(api.GET('/v1/flows/{id}/diff', { params: { path: { id }, query: { from, to } } }));

export const discardDraft = (id: string, revision: number): Promise<TFlowDraft> =>
  unwrap(api.DELETE('/v1/flows/{id}/draft', { params: { path: { id }, query: { revision } } }));

export const restoreDraft = (
  id: string,
  body: { versionNumber: number; revision: number },
): Promise<TFlowDraft> =>
  unwrap(api.POST('/v1/flows/{id}/draft/restore', { params: { path: { id } }, body }));

export const carryOverDraft = (id: string, revision: number): Promise<TCarryOver> =>
  unwrap(
    api.POST('/v1/flows/{id}/draft/carry-over', {
      params: { path: { id } },
      body: { revision, dryRun: false },
    }),
  );

export type TTestRun = components['schemas']['TestRunResDto'];
export type TTestStep = components['schemas']['TestStepDto'];
export type TTestSampleSpec = components['schemas']['TestSampleSpecDto'];
export type TDraftTestRequest = components['schemas']['DraftTestReqDto'];
export type TBacktest = components['schemas']['BacktestResDto'];
export type TCanvasStats = components['schemas']['CanvasStatsResDto'];
export type TNodeStats = components['schemas']['NodeStatsDto'];
export type TStatsWindow = TCanvasStats['window'];
export type TManualRun = components['schemas']['ManualRunResDto'];
export type TFlowInputField = components['schemas']['ManualInputFieldDto'];

export const runDraftTest = (id: string, body: TDraftTestRequest): Promise<TTestRun> =>
  unwrap(api.POST('/v1/flows/{id}/draft/test', { params: { path: { id } }, body }));

export const backtestNode = (
  id: string,
  key: string,
  body: components['schemas']['BacktestReqDto'],
): Promise<TBacktest> =>
  unwrap(
    api.POST('/v1/flows/{id}/draft/nodes/{key}/backtest', { params: { path: { id, key } }, body }),
  );

export type TCodeRun = components['schemas']['CodeRunResDto'];

export const runCodeStep = (
  id: string,
  key: string,
  body: components['schemas']['TestActionReqDto'],
): Promise<TCodeRun> =>
  unwrap(
    api.POST('/v1/flows/{id}/draft/nodes/{key}/run-code', { params: { path: { id, key } }, body }),
  );

export const getCanvasStats = (id: string, window: TStatsWindow): Promise<TCanvasStats> =>
  unwrap(api.GET('/v1/flows/{id}/stats', { params: { path: { id }, query: { window } } }));

export const startManualRun = (id: string, input: Record<string, unknown>): Promise<TManualRun> =>
  unwrap(api.POST('/v1/flows/{id}/runs', { params: { path: { id } }, body: { input } }));

export const flowKeys = {
  credentials: ['credentials'] as const,
  list: (query: TFlowQuery = {}) => ['flows', query] as const,
  one: (id: string) => ['flows', id] as const,
  draft: (id: string) => ['flows', id, 'draft'] as const,
  samples: (id: string) => ['flows', id, 'draft', 'samples'] as const,
  versions: (id: string) => ['flows', id, 'versions'] as const,
  version: (id: string, number: number | 'live') => ['flows', id, 'versions', number] as const,
  diff: (id: string, from: string, to: string) => ['flows', id, 'diff', from, to] as const,
  catalog: ['flows', 'node-types'] as const,
  stats: (id: string, window: TStatsWindow) => ['flows', id, 'stats', window] as const,
  // Not under `flows`: that prefix is invalidated on every run frame; a backtest reruns on edits only.
  backtest: (id: string, key: string, graphHash: string) =>
    ['flow-backtest', id, key, graphHash] as const,
  // Outside `flows` for the same reason: a run frame must not rescan 50 requests.
  knownFields: (id: string) => ['flow-known-fields', id] as const,
  resultFields: (id: string) => ['flow-result-fields', id] as const,
  sampleTrigger: (id: string, query: TSampleTriggerQuery) =>
    ['flow-sample-trigger', id, query.kind, query.bookmarkId ?? '', query.requestId ?? ''] as const,
};
